/** Layout and state primitives. No chart logic here — see charts.tsx. */
import type { ReactNode } from 'react'
import './ui.css'

export function Tile({ title, hint, children, span, action }:
  { title: string; hint?: ReactNode; children: ReactNode; span?: boolean | undefined
    /** A control that belongs to this tile — a refresh button, say. Placed in
     *  the header so it reads as part of the instrument rather than floating
     *  above the data it acts on. */
    action?: ReactNode }) {
  return (
    <section className="tile" style={span ? { gridColumn: '1 / -1' } : undefined}>
      <header className="tile-h">
        <span>{title}</span>
        {hint ? <em className="mono">{hint}</em> : null}
        {action ? <span className="tile-a">{action}</span> : null}
      </header>
      {children}
    </section>
  )
}

/* 'advisory' is DESIGN.md's third alarm tier (crisis/warning/advisory),
 * added alongside the qdrant clients panel so a caller's own bad request
 * (4xx) can read as a genuinely different severity from both a server
 * failure (crit) and backpressure (warn) — see QdrantStatusMix's
 * CLASS_STATE and QdrantClients, the two places that now share it. */
export type State = 'ok' | 'warn' | 'crit' | 'advisory' | 'idle'

export const Pill = ({ state, children }: { state: State; children: ReactNode }) => (
  <span className={`pill ${state}`}>{children}</span>
)

export function Section({ title, hint, id }:
  { title: string; hint?: string | undefined; id?: string | undefined }) {
  return (
    /* `id` is the scroll target for the flow board: clicking a stage jumps to
     * the section detailing it. scroll-margin-top keeps the heading clear of
     * the sticky header rather than tucked under it. */
    <div className="sec" id={id} style={id ? { scrollMarginTop: 72 } : undefined}>
      <h2>{title}</h2>
      <span className="rule" />
      {hint ? <span className="hint mono">{hint}</span> : null}
    </div>
  )
}

/**
 * Panels fail independently. One dead endpoint must degrade its own tile and
 * leave the rest of the board readable — a single error boundary around the
 * whole page would blank everything because ES was slow.
 */
export function PanelState({ isLoading, error, children }:
  { isLoading: boolean; error: unknown; children: ReactNode }) {
  if (error) {
    const msg = error instanceof Error ? error.message : String(error)
    return <p className="panel-err">unavailable — {msg}</p>
  }
  /* A skeleton in the shape of what is arriving, not the word "loading". It also
     reserves the space, so a panel does not jump when its figure lands. The
     role/label pair is what a screen reader gets instead of two blank bars. */
  if (isLoading) {
    return (
      <div className="skel" role="status" aria-label="loading">
        <i /><i />
      </div>
    )
  }
  return <>{children}</>
}

/** A labelled figure. `state` colours the number, never the label. */
export function Stat({ label, value, unit, state, sub, title }: {
  /** A node, not only text: a label a finding names carries its mark (the
   *  figure beside it may be a window the finding never judged). */
  label: ReactNode; value: ReactNode; unit?: string | undefined
  state?: State | undefined; sub?: ReactNode
  /** The exact figure on hover, for a `value` that is not a bare string or
   *  number (a Fragment carrying a mark beside its figure has none to derive). */
  title?: string | undefined
}) {
  return (
    <div className="stat">
      {/* title carries the exact figure. .stat-n may ellipsise a very long
        * value at narrow widths, and a truncated number you cannot recover is
        * worse than one that is merely small. */}
      <div className={`stat-n tnum mono${state ? ` ${state}` : ''}`}
           title={title ?? (typeof value === 'string' || typeof value === 'number'
             ? `${value}${unit ?? ''}` : undefined)}>
        {value}{unit ? <i>{unit}</i> : null}
      </div>
      <div className="stat-l">{label}</div>
      {sub ? <div className="stat-s mono">{sub}</div> : null}
    </div>
  )
}

/** Horizontal proportion bar. Widths are clamped so bad data cannot overflow. */
export function Bar({ pct, state = 'ok' }: { pct: number; state?: State | undefined }) {
  return (
    <div className="bar">
      <i className={state} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
    </div>
  )
}
